import React, { useEffect, useState } from "react";
import { adminApi } from "./api";
import { toast } from "sonner";
import { parseApiError } from "../lib/apiError";
import {
  ShieldCheck, Zap, ExternalLink, Loader2, AlertTriangle, Unlink,
  Info, CircleDollarSign, Lock, Save, Building2, ChevronDown, Sparkles, Trash2,
} from "lucide-react";

const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] ring-1 ${
    ok ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
       : "bg-slate-100 text-slate-500 ring-slate-200"
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-400"}`} />
    {label}
  </span>
);

const SERIF = { fontFamily: "'Cormorant Garamond', serif" };

export default function AdminSettings() {
  return (
    <div className="p-10 max-w-5xl mx-auto space-y-8" data-testid="admin-settings">
      <header>
        <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700">settings</p>
        <h1 className="font-serif text-4xl text-slate-900 mt-1" style={SERIF}>
          Your studio settings
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          Manage your session pricing, business details and payment status — everything you need to run Kama Loka Journeys.
        </p>
      </header>

      <PricingCard />
      <BusinessCard />
      <StripeCard />
      <MaintenanceCard />
    </div>
  );
}

/* ---------------- Production launch (danger zone) ---------------- */
function MaintenanceCard() {
  const [resetting, setResetting] = useState(false);

  const reset = async () => {
    const typed = window.prompt(
      "This permanently deletes ALL blueprints, transactions, bookings, client intakes and analytics.\n" +
      "Your settings (prices, availability, Stripe connection) are kept.\n\n" +
      'Type RESET to confirm:'
    );
    if (typed === null) return;
    if (typed.trim() !== "RESET") {
      toast.error('Cancelled — you must type RESET exactly.');
      return;
    }
    setResetting(true);
    try {
      const r = await adminApi.post("/maintenance/reset-data", { confirm: "RESET" });
      const d = r.data.deleted || {};
      toast.success(
        `Clean slate ready: ${d.profiles || 0} blueprints, ${d.payment_transactions || 0} transactions, ` +
        `${d.bookings || 0} bookings, ${d.events || 0} analytics events removed.`
      );
    } catch (e) {
      toast.error(parseApiError(e, "Reset failed"));
    } finally { setResetting(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-rose-200 overflow-hidden" data-testid="maintenance-card">
      <div className="px-7 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
            <Trash2 className="w-5 h-5" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-rose-500">Production launch</p>
            <h2 className="font-serif text-xl text-slate-900" style={SERIF}>Clean all test data</h2>
          </div>
        </div>
        <button
          data-testid="reset-data-btn"
          onClick={reset}
          disabled={resetting}
          className="px-5 py-2 rounded-full border border-rose-300 text-sm text-rose-700 flex items-center gap-2 hover:bg-rose-50 disabled:opacity-50 transition-colors"
        >
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          Reset data…
        </button>
      </div>
      <div className="px-7 pb-6">
        <p className="text-sm text-slate-500">
          One-time cleanup before going live: removes every test blueprint, transaction, booking, intake and
          analytics event. Your prices, availability, business details and Stripe connection are <span className="font-medium text-slate-700">not</span> touched.
          This cannot be undone.
        </p>
      </div>
    </section>
  );
}

/* ---------------- Session pricing ---------------- */
function PricingCard() {
  const [packages, setPackages] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/pricing").then((r) => {
      setPackages(r.data.packages || []);
      const init = {};
      (r.data.packages || []).forEach((p) => { init[p.id] = String(p.amount); });
      setEdits(init);
    });
  }, []);

  const dirty = packages && packages.some((p) => Number(edits[p.id]) !== p.amount);
  const invalid = packages && packages.some((p) => {
    const v = Number(edits[p.id]);
    return !Number.isFinite(v) || v < 1 || v > 10000;
  });

  const save = async () => {
    setSaving(true);
    try {
      const prices = {};
      packages.forEach((p) => { prices[p.id] = Number(edits[p.id]); });
      await adminApi.put("/pricing", { prices });
      setPackages(packages.map((p) => ({ ...p, amount: Number(edits[p.id]) })));
      toast.success("Session prices updated — live on the website immediately.");
    } catch (e) {
      toast.error(parseApiError(e, "Failed to save prices"));
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="pricing-card">
      <div className="px-7 py-5 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <CircleDollarSign className="w-5 h-5" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Pricing</p>
            <h2 className="font-serif text-xl text-slate-900" style={SERIF}>Session prices</h2>
          </div>
        </div>
        <button
          data-testid="pricing-save-btn"
          onClick={save}
          disabled={!dirty || invalid || saving}
          className="px-5 py-2 rounded-full bg-slate-900 text-white text-sm flex items-center gap-2 hover:bg-slate-700 disabled:opacity-40 transition-colors"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save prices
        </button>
      </div>

      <div className="p-7 space-y-3">
        {!packages ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <>
            {packages.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-5 py-4" data-testid={`price-row-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-slate-900 font-medium truncate">{p.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">100 % goes to your Stripe account</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-slate-400 text-sm">$</span>
                  <input
                    data-testid={`price-input-${p.id}`}
                    type="number"
                    min="1"
                    max="10000"
                    step="1"
                    value={edits[p.id] ?? ""}
                    onChange={(e) => setEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                    className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-right text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
                  />
                  <span className="text-slate-500 text-sm w-10">{p.currency}</span>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between gap-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-5 py-4" data-testid="price-row-blueprint">
              <div className="flex items-center gap-3 min-w-0">
                <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-slate-700 font-medium truncate">Soul Blueprint — Full Unlock</p>
                  <p className="text-xs text-slate-400 mt-0.5">$40.00 to you · $4.44 platform fee</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-500 shrink-0">
                <Lock className="w-3.5 h-3.5" />
                <span className="tabular-nums font-medium text-slate-700">$44.44 AUD</span>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ---------------- Business details ---------------- */
function BusinessCard() {
  const [form, setForm] = useState({ notification_email: "", phone: "", timezone: "Australia/Brisbane" });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/business").then((r) => {
      setForm({
        notification_email: r.data.notification_email || "",
        phone: r.data.phone || "",
        timezone: r.data.timezone || "Australia/Brisbane",
      });
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/business", form);
      toast.success("Business details saved.");
    } catch {
      toast.error("Failed to save business details");
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="business-card">
      <div className="px-7 py-5 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Business</p>
            <h2 className="font-serif text-xl text-slate-900" style={SERIF}>Contact & notifications</h2>
          </div>
        </div>
        <button
          data-testid="business-save-btn"
          onClick={save}
          disabled={!loaded || saving}
          className="px-5 py-2 rounded-full bg-slate-900 text-white text-sm flex items-center gap-2 hover:bg-slate-700 disabled:opacity-40 transition-colors"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>
      <div className="p-7 grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2">Notification email</p>
          <input
            data-testid="business-email"
            type="email"
            value={form.notification_email}
            onChange={(e) => setForm((s) => ({ ...s, notification_email: e.target.value }))}
            placeholder="kassie@…"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
          />
          <p className="mt-1.5 text-[11px] text-slate-400">Booking & payment alerts will be sent here (email notifications coming soon).</p>
        </label>
        <label className="block">
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2">Business phone</p>
          <input
            data-testid="business-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
            placeholder="+61 …"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
          />
        </label>
        <label className="block">
          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2">Timezone</p>
          <select
            data-testid="business-timezone"
            value={form.timezone}
            onChange={(e) => setForm((s) => ({ ...s, timezone: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
          >
            {["Australia/Brisbane", "Australia/Sydney", "Australia/Melbourne", "Australia/Adelaide", "Australia/Perth", "Australia/Darwin", "Australia/Hobart"].map((tz) => (
              <option key={tz} value={tz}>{tz.replace("Australia/", "")} (AU)</option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}

/* ---------------- Stripe (compact + advanced) ---------------- */
function StripeCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [err, setErr] = useState("");

  const load = () => adminApi.get("/stripe/status")
    .then((r) => setStatus(r.data))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const startConnect = async () => {
    setConnectLoading(true); setErr("");
    try {
      const r = await adminApi.get("/stripe/connect-url");
      window.location.href = r.data.url;
    } catch (e) {
      setErr(parseApiError(e, "Failed to start Stripe Connect flow"));
      setConnectLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Stripe account? Payments will stop routing until reconnected.")) return;
    setDisconnecting(true);
    try {
      await adminApi.delete("/stripe/connect");
      await load();
    } finally { setDisconnecting(false); }
  };

  const isConnected = status?.connected && status?.charges_enabled && !status?.health_error;

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="stripe-connect-card">
      <div className="px-7 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#635BFF]/10 text-[#635BFF] flex items-center justify-center">
            <CircleDollarSign className="w-5 h-5" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Payments</p>
            <h2 className="font-serif text-xl text-slate-900" style={SERIF}>Stripe</h2>
          </div>
        </div>
        {loading
          ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          : isConnected
            ? <StatusBadge ok label="Payments active" />
            : <StatusBadge ok={false} label="Setup needed" />}
      </div>

      <div className="px-7 pb-5">
        <p className="text-sm text-slate-500">
          Refunds, payouts and receipts are managed directly in your{" "}
          <a
            href={status?.account_id ? `https://dashboard.stripe.com/${status.account_id}` : "https://dashboard.stripe.com"}
            target="_blank" rel="noreferrer"
            className="text-slate-900 underline underline-offset-2 hover:no-underline inline-flex items-center gap-1"
            data-testid="stripe-dashboard-link"
          >
            Stripe dashboard <ExternalLink className="w-3 h-3" />
          </a>.
        </p>
      </div>

      <details className="border-t border-slate-100 group" data-testid="stripe-advanced">
        <summary className="px-7 py-4 text-xs uppercase tracking-[0.18em] text-slate-500 cursor-pointer flex items-center gap-2 hover:text-slate-700 select-none list-none">
          <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
          Advanced — connection setup
        </summary>
        <div className="px-7 pb-7">
          {isConnected ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <MiniStat label="Account" value={status.account_id} mono />
                <MiniStat label="Business" value={status.business_name || "—"} />
                <div className="rounded-xl border border-slate-200 p-4 grid grid-cols-2 gap-2 items-center">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Charges</p>
                    <StatusBadge ok={!!status.charges_enabled} label={status.charges_enabled ? "Enabled" : "Disabled"} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Payouts</p>
                    <StatusBadge ok={!!status.payouts_enabled} label={status.payouts_enabled ? "Enabled" : "Disabled"} />
                  </div>
                </div>
              </div>
              <button
                onClick={disconnect}
                data-testid="stripe-disconnect"
                disabled={disconnecting}
                className="px-4 py-2 rounded-full border border-rose-200 text-sm text-rose-700 flex items-center gap-2 hover:bg-rose-50 disabled:opacity-50"
              >
                {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                Disconnect account
              </button>
            </>
          ) : (
            <>
              {status?.connected && (status?.health_error || !status?.charges_enabled) && (
                <div className="mb-5 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">
                      {status.health_error
                        ? "This platform is not authorised on the Stripe account yet."
                        : "The Stripe account isn't fully activated yet."}
                    </p>
                    <button onClick={load} className="mt-2 text-xs underline hover:no-underline">Refresh status</button>
                  </div>
                </div>
              )}
              <div className="mb-5 space-y-3">
                <Row icon={ShieldCheck} title="No secret sharing" text="Authorisation happens on Stripe's official OAuth screen — no passwords or API keys shared." />
                <Row icon={Zap} title="Instant, atomic split" text="Blueprint payments split automatically: $40.00 to Kassie / $4.44 platform fee. Sessions: 100 % to Kassie." />
                <Row icon={Info} title="Revocable anytime" text="Access can be revoked from the Stripe dashboard at any time." />
              </div>
              <button
                onClick={startConnect}
                disabled={connectLoading}
                data-testid="stripe-connect-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#635BFF] hover:bg-[#5148d9] text-white font-medium text-sm shadow-sm transition-colors disabled:opacity-60"
              >
                {connectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                {connectLoading ? "Redirecting to Stripe…" : "Connect Kassie's Stripe account"}
              </button>
              {err && (
                <p className="mt-3 text-sm text-rose-700 inline-flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> {err}
                </p>
              )}
            </>
          )}
        </div>
      </details>
    </section>
  );
}

const MiniStat = ({ label, value, mono }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
    <p className={`mt-1 text-slate-900 truncate ${mono ? "font-mono text-sm break-all" : ""}`}>{value}</p>
  </div>
);

const Row = ({ icon: Icon, title, text }) => (
  <div className="flex items-start gap-3">
    <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4" />
    </span>
    <div>
      <p className="text-sm font-medium text-slate-900">{title}</p>
      <p className="text-xs text-slate-500 mt-0.5">{text}</p>
    </div>
  </div>
);
